import { TemplateVariableType } from "./AgentRecord";

/** A kind of record whose page can launch an agent on it. */
export type LaunchSubjectKind = "dataset" | "session";

/**
 * For each kind of launch subject: the type of the agent variables that take
 * its id, and the launch page's query parameter that carries that id.
 */
export const LAUNCH_SUBJECT_KINDS: Record<
  LaunchSubjectKind,
  { variableType: TemplateVariableType; queryParam: string }
> = {
  dataset: {
    variableType: TemplateVariableType.DATASET_ID,
    queryParam: "dataset_id",
  },
  session: {
    variableType: TemplateVariableType.SESSION_ID,
    queryParam: "session_id",
  },
};

const KINDS = Object.keys(LAUNCH_SUBJECT_KINDS) as LaunchSubjectKind[];

/** The record an agent is launched on, which its launch page pre-fills. */
export interface LaunchSubject {
  /** Kind of record; picks the variable type and query parameter from ``LAUNCH_SUBJECT_KINDS``. */
  kind: LaunchSubjectKind;
  /** The record's id, filled into every variable of that type. */
  id: string;
}

/** The subject ids a launch link carries, by kind. */
export type LaunchSeed = Partial<Record<LaunchSubjectKind, string>>;

/** The kind whose id a variable of `type` takes, or `undefined` for none. */
export function launchSubjectKindFor(
  type: TemplateVariableType,
): LaunchSubjectKind | undefined {
  return KINDS.find((kind) => LAUNCH_SUBJECT_KINDS[kind].variableType === type);
}

/** The seed as the launch page's search params; empty ids are left out. */
export function launchSeedToSearchParams(seed: LaunchSeed): URLSearchParams {
  const params = new URLSearchParams();
  for (const kind of KINDS) {
    const id = seed[kind];
    if (id) {
      params.set(LAUNCH_SUBJECT_KINDS[kind].queryParam, id);
    }
  }
  return params;
}

/** The seed the launch page's search params carry; empty values are left out. */
export function launchSeedFromSearchParams(
  params: URLSearchParams,
): LaunchSeed {
  const seed: LaunchSeed = {};
  for (const kind of KINDS) {
    const id = params.get(LAUNCH_SUBJECT_KINDS[kind].queryParam);
    if (id) {
      seed[kind] = id;
    }
  }
  return seed;
}
